import React from 'react';
import { isEmpty } from 'lodash';
import {
  ContentState,
  convertFromHTML,
  Editor,
  EditorState,
  RichUtils
} from 'draft-js';
import style from './editorStyle.scss';
import classnames from 'classnames';
import { getRoleOfLoggedInUser } from './investDetailHeaderUtil';
import { getAlertIDFromURL } from '../../../../services/util';
import AlertPopup from '../../../../components/AlertPopup';

/**
 * DEFAULT_EDITOR_TEXT_SIZE , max length of text which editor will support
 * @type {number}
 */
const DEFAULT_EDITOR_TEXT_SIZE = 5000;

/**
 * TookitakiRichEditor component
 */
export default class TookitakiRichEditor extends React.Component {
  /**
   * constructor
   * @param props
   */
  constructor(props) {
    super(props);
    this.state = {
      readOnlyEditorState: EditorState.createEmpty(),
      editorState: EditorState.createEmpty(),
      showAlertPopUp: false,
      alertMsg: ''
    };

    this.focus = () => this.refs.editor.focus();
    this.handleKeyCommand = (command) => this._handleKeyCommand(command);
    this.onTab = (e) => this._onTab(e);
    this.toggleBlockType = (type) => this._toggleBlockType(type);
    this.toggleInlineStyle = (style) => this._toggleInlineStyle(style);
    let _self = this;
    this.onChange = (editorState) => {
      const contentState = editorState.getCurrentContent();
      if (contentState.getPlainText().length > DEFAULT_EDITOR_TEXT_SIZE) {
        editorState = EditorState.undo(editorState);
        this.toggleAlert(
          'Investigation Notes cannot be more than 5000 characters.'
        );
      }
      this.setState({ editorState }, () => {
        const innerHtml = _self.refs.editor.editor.innerHTML;
        const textContent = _self.refs.editor.editor.textContent;
        this.props.onEditorTextChange(innerHtml, textContent);
      });
    };
  }

  componentDidMount() {
    const { investigatorNote = '', supervisorNote = '' } =
      this.props.headerAlertDetailObject || {};
    const {
      analystEditorModalWidgetPerm,
      superVisorEditorModalWidgetPerm
    } = this.props;
    const { isWrite: isAnWrite, isReadOnly: isAnReadOnly } =
      analystEditorModalWidgetPerm || {};
    const { isWrite: isSuWrite, isReadOnly: isSuReadOnly } =
      superVisorEditorModalWidgetPerm || {};

    const { isEmpty, content } = this._getNotesFromLS(
      isSuWrite,
      isAnWrite,
      supervisorNote,
      investigatorNote
    );
    this.setState({
      readOnlyEditorState: this.getReadOnlyEditorState(
        isSuWrite,
        isAnWrite,
        supervisorNote,
        investigatorNote,
        isSuReadOnly,
        isAnReadOnly
      ),
      editorState: isEmpty
        ? EditorState.createEmpty()
        : EditorState.createWithContent(content)
    });
  }

  getReadOnlyEditorState = (
    isSuWrite,
    isAnWrite,
    supervisorNote,
    investigatorNote,
    isSuReadOnly,
    isAnReadOnly
  ) => {
    const eState = EditorState.createEmpty();
    if (isAnWrite) {
      return EditorState.createWithContent(this._formatContent(supervisorNote));
    }
    if (isSuWrite || isSuReadOnly || isAnReadOnly) {
      return EditorState.createWithContent(
        this._formatContent(investigatorNote)
      );
    }
    return eState;
  };

  /**
   * _formatContent
   * @param content
   * @returns {ContentState}
   * @private
   */
  _formatContent = (content) => {
    const blocksFromHTML = convertFromHTML(content);
    return ContentState.createFromBlockArray(
      blocksFromHTML.contentBlocks,
      blocksFromHTML.entityMap
    );
  };

  _getNotesFromLS = (
    isSuperVisor,
    isAnalyst,
    supervisorGeneratedNote,
    investigatorGeneratedNote
  ) => {
    const { savedNote = {} } = this.props;
    let notes = '';
    const { alertID } = getAlertIDFromURL();
    if (isSuperVisor) {
      // check if saved notes are there...
      const { noteDetails = '', alertID: alert_id = '' } = savedNote;
      if (alertID === alert_id) {
        const { html = '', text = '' } = noteDetails || {};
        if (!isEmpty(text)) {
          notes = html;
        }
      }
      if (isEmpty(notes) && !isEmpty(supervisorGeneratedNote)) {
        notes = supervisorGeneratedNote;
      }
    }
    if (isAnalyst) {
      const { noteDetails = '', alertID: alert_id = '' } = savedNote || {};
      if (alertID === alert_id) {
        const { html = '', text = '' } = noteDetails || {};
        if (!isEmpty(text)) {
          notes = html;
        }
      }
      if (isEmpty(notes) && !isEmpty(investigatorGeneratedNote)) {
        notes = investigatorGeneratedNote;
      }
    }

    if (!isAnalyst && !isSuperVisor && !isEmpty(supervisorGeneratedNote)) {
      notes = supervisorGeneratedNote;
    }

    if (isEmpty(notes)) {
      return {
        isEmpty: true,
        content: EditorState.createEmpty()
      };
    }

    return {
      isEmpty: false,
      content: this._formatContent(notes)
    };
  };

  /**
   * _handleKeyCommand
   * @param command
   * @returns {boolean}
   * @private
   */
  _handleKeyCommand(command) {
    const { editorState } = this.state;
    const newState = RichUtils.handleKeyCommand(editorState, command);
    if (newState) {
      this.onChange(newState);
      return true;
    }
    return false;
  }

  /**
   * _onTab
   * @param e
   * @private
   */
  _onTab(e) {
    const maxDepth = 4;
    this.onChange(RichUtils.onTab(e, this.state.editorState, maxDepth));
  }

  /**
   * _toggleBlockType
   * @param blockType
   * @private
   */
  _toggleBlockType(blockType) {
    this.onChange(RichUtils.toggleBlockType(this.state.editorState, blockType));
  }

  /**
   * _toggleInlineStyle
   * @param inlineStyle
   * @private
   */
  _toggleInlineStyle(inlineStyle) {
    this.onChange(
      RichUtils.toggleInlineStyle(this.state.editorState, inlineStyle)
    );
  }

  /**
   * toggleAlert box
   * @param msg
   * @returns {boolean}
   */
  toggleAlert = (msg = '') => {
    this.setState({
      showAlertPopUp: !this.state.showAlertPopUp,
      alertMsg: msg
    });
    return false;
  };

  /**
   * render
   * @returns {*}
   */
  render() {
    const { editorState, readOnlyEditorState } = this.state;
    const {
      analystEditorModalWidgetPerm,
      superVisorEditorModalWidgetPerm
    } = this.props;
    const { isWrite: isAnWrite } = analystEditorModalWidgetPerm || {};
    const { isWrite: isSuWrite } = superVisorEditorModalWidgetPerm || {};
    let editorTitle = '';
    if (isSuWrite) {
      editorTitle = 'Supervisor Notes';
    }
    if (isAnWrite) {
      editorTitle = 'Investigator Notes';
    }

    let showReadonlyEditor;
    let showWriteEditor;
    let disableWriteOnlyEditor;
    if (isSuWrite) {
      showReadonlyEditor = true;
      showWriteEditor = true;
      disableWriteOnlyEditor = false;
    }
    if (isAnWrite) {
      showReadonlyEditor = true;
      showWriteEditor = true;
      disableWriteOnlyEditor = false;
    }
    if (!isSuWrite && !isAnWrite) {
      const { isAnalyst, isSuperVisor } = getRoleOfLoggedInUser();
      const { alertStatus } = this.props.headerAlertDetailObject || {};
      // val Open: Short               = 0
      // val Closed: Short             = 1
      // val Assigned: Short           = 2
      // val UnderInvestigation: Short = 3
      // val PendingApproval: Short    = 4

      if (alertStatus === 1) {
        if (isAnalyst || isSuperVisor) {
          showReadonlyEditor = true;
          showWriteEditor = true;
          disableWriteOnlyEditor = true;
        } else {
          showReadonlyEditor = false;
          showWriteEditor = false;
          disableWriteOnlyEditor = true;
        }
      }
      if (alertStatus === 0 || alertStatus === 2 || alertStatus === 3) {
        showReadonlyEditor = false;
        showWriteEditor = false;
        disableWriteOnlyEditor = true;
      }
      if (alertStatus === 4) {
        if (isAnalyst) {
          showReadonlyEditor = true;
          showWriteEditor = false;
          disableWriteOnlyEditor = true;
        }
      }
    }

    // If the user changes block type before entering any text, we can
    // either style the placeholder or hide it. Let's just hide it now.
    let className = 'RichEditor-editor';
    var contentState = editorState.getCurrentContent();
    if (!contentState.hasText()) {
      if (contentState.getBlockMap().first().getType() !== 'unstyled') {
        className += ' RichEditor-hidePlaceholder';
      }
    }
    /**
     * return func
     */
    return (
      <div className="RichEditor-root">
        {this.state.showAlertPopUp && (
          <AlertPopup
            toggleAlert={this.toggleAlert}
            content={this.state.alertMsg}
          />
        )}
        <BlockStyleControls
          editorState={editorState}
          onToggle={this.toggleBlockType}
        />
        <InlineStyleControls
          editorState={editorState}
          onToggle={this.toggleInlineStyle}
        />
        {showReadonlyEditor && (
          <div>
            <div className={style.editorTitle}>
              {isAnWrite ? 'Supervisor Notes' : 'Investigator Notes'}
            </div>
            <div
              style={{
                backgroundColor: 'rgb(0,0,0,0.06)',
                cursor: 'not-allowed'
              }}
              className={classnames(className)}>
              <Editor
                blockStyleFn={getBlockStyle}
                customStyleMap={styleMap}
                editorState={readOnlyEditorState}
                readOnly={true}
              />
            </div>
          </div>
        )}

        {showWriteEditor && (
          <div>
            <div className={classnames(style.editorTitle, style.marTop20)}>
              {editorTitle}
            </div>
            <div className={className} onClick={this.focus}>
              <Editor
                blockStyleFn={getBlockStyle}
                customStyleMap={styleMap}
                editorState={editorState}
                handleKeyCommand={this.handleKeyCommand}
                onChange={this.onChange}
                onTab={this.onTab}
                placeholder=""
                ref="editor"
                spellCheck={true}
                readOnly={disableWriteOnlyEditor}
              />
            </div>
          </div>
        )}
      </div>
    );
  }
}
/**
 * styleMap
 * @type {{CODE: {padding: number, backgroundColor: string, fontFamily: string, fontSize: number}}}
 */
// Custom overrides for "code" style.
const styleMap = {
  CODE: {
    backgroundColor: 'rgba(0, 0, 0, 0.05)',
    fontFamily: '"Inconsolata", "Menlo", "Consolas", monospace',
    fontSize: 16,
    padding: 2
  }
};

/**
 * getBlockStyle
 * @param block
 * @returns {string|null}
 */
function getBlockStyle(block) {
  switch (block.getType()) {
    case 'blockquote':
      return 'RichEditor-blockquote';
    default:
      return null;
  }
}

/**
 * StyleButton component
 */
class StyleButton extends React.Component {
  /**
   * constructor
   */
  constructor() {
    super();
    this.onToggle = (e) => {
      e.preventDefault();
      this.props.onToggle(this.props.style);
    };
  }

  /**
   * render
   * @returns {*}
   */
  render() {
    let className = 'RichEditor-styleButton';
    if (this.props.active) {
      className += ' RichEditor-activeButton';
    }

    return (
      <span className={className} onMouseDown={this.onToggle}>
        {this.props.label}
      </span>
    );
  }
}

/**
 * BLOCK_TYPES
 * @type {({style: string, label: string}|{style: string, label: string}|{style: string, label: string}|{style: string, label: string}|{style: string, label: string})[]}
 */
const BLOCK_TYPES = [
  { label: 'H1', style: 'header-one' },
  { label: 'H2', style: 'header-two' },
  { label: 'H3', style: 'header-three' },
  { label: 'H4', style: 'header-four' },
  { label: 'H5', style: 'header-five' },
  { label: 'H6', style: 'header-six' },
  { label: 'Blockquote', style: 'blockquote' },
  { label: 'UL', style: 'unordered-list-item' },
  { label: 'OL', style: 'ordered-list-item' },
  { label: 'Code Block', style: 'code-block' }
];

/**
 * BlockStyleControls
 * @param props
 * @returns {*}
 * @constructor
 */
const BlockStyleControls = (props) => {
  const { editorState } = props;
  const selection = editorState.getSelection();
  const blockType = editorState
    .getCurrentContent()
    .getBlockForKey(selection.getStartKey())
    .getType();

  return (
    <div className="RichEditor-controls">
      {BLOCK_TYPES.map((type) => (
        <StyleButton
          key={type.label}
          active={type.style === blockType}
          label={type.label}
          onToggle={props.onToggle}
          style={type.style}
        />
      ))}
    </div>
  );
};
/**
 * INLINE_STYLES
 * @type {({style: string, label: string}|{style: string, label: string}|{style: string, label: string}|{style: string, label: string})[]}
 */
var INLINE_STYLES = [
  { label: 'Bold', style: 'BOLD' },
  { label: 'Italic', style: 'ITALIC' },
  { label: 'Underline', style: 'UNDERLINE' },
  { label: 'Monospace', style: 'CODE' }
];

/**
 * InlineStyleControls
 * @param props
 * @returns {*}
 * @constructor
 */
const InlineStyleControls = (props) => {
  var currentStyle = props.editorState.getCurrentInlineStyle();
  return (
    <div className="RichEditor-controls">
      {INLINE_STYLES.map((type) => (
        <StyleButton
          key={type.label}
          active={currentStyle.has(type.style)}
          label={type.label}
          onToggle={props.onToggle}
          style={type.style}
        />
      ))}
    </div>
  );
};
